import { Component, OnInit, OnDestroy, ViewChild, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService, SortMeta } from 'primeng/api';
import { OrdenService } from '../../services/orden/orden.service';
import { Orden } from '../../domain/orden.domain';
import { CurrencyFormatPipe } from '../../pipes/currency-format.pipe';
import { DateCustomPipe } from '../../pipes/date-custom.pipe';
import { environment } from '../../../environments/environment';
import { Subject, takeUntil } from 'rxjs';

interface Column {
  field: string;
  header: string;
  fieldType?: 'text' | 'number' | 'date';
  sortable?: boolean;
  exportable?: boolean;
  filtrable?: boolean;
  modeDisplayFilter?: 'menu' | 'row';
}

@Component({
  selector: 'app-ordenes',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    TooltipModule,
    ConfirmDialogModule,
    CurrencyFormatPipe,
    DateCustomPipe
  ],
  providers: [ConfirmationService],
  templateUrl: './ordenes.component.html',
  styleUrls: ['./ordenes.component.scss']
})
export class OrdenesComponent implements OnInit, OnDestroy {
  @ViewChild('dt') table: Table | undefined;

  ordenes = signal<Orden[]>([]);
  loading = signal<boolean>(false);
  totalRecords = signal<number>(0);
  selectedRows: Orden[] = [];
  filterValue: string = '';

  nroFilasPorPagina: number = environment.REGISTROS_PER_PAGE || 10;
  paginaActual = 0;
  paginate = true;
  rowsPerPageOptions: number[] = [];

  multiSortMeta: SortMeta[] = [];
  filtersMetadata: any = {};

  private destroy$ = new Subject<void>();
  private errorCount = 0;
  private readonly MAX_ERROR_COUNT = 3;
  private lastErrorTimestamp = 0;
  private readonly ERROR_RESET_TIMEOUT = 60000;

  columnsTable: Column[] = [
    { field: 'numero', header: 'Número', fieldType: 'text', sortable: true, exportable: true, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'clientePrincipal', header: 'Cliente principal', fieldType: 'text', sortable: false, exportable: true, filtrable: false },
    { field: 'vigencia', header: 'Vigencia', fieldType: 'date', sortable: false, exportable: false, filtrable: false },
    { field: 'estado', header: 'Estado', fieldType: 'text', sortable: true, exportable: true, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'tipoOrden', header: 'Tipo de orden', fieldType: 'text', sortable: true, exportable: true, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'montoTotal', header: 'Monto total', fieldType: 'number', sortable: true, exportable: true, filtrable: false },
    { field: 'accion', header: 'Acciones', sortable: false, filtrable: false }
  ];

  constructor(
    private ordenService: OrdenService,
    private router: Router,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {
    if (this.rowsPerPageOptions.length === 0) {
      this.rowsPerPageOptions = environment.REGISTROS_PER_PAGE_OPTIONS || [10, 20, 30, 50];
    }
  }

  ngOnInit(): void {
    this.loading.set(false);
    this.errorCount = 0;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadLazy(event: TableLazyLoadEvent): void {
    if (event.first !== undefined && event.rows !== null && event.rows !== undefined) {
      this.paginaActual = Math.floor(event.first / event.rows) + 1;
      this.nroFilasPorPagina = event.rows;
    } else {
      this.paginaActual = 1;
      this.nroFilasPorPagina = environment.REGISTROS_PER_PAGE ?? 10;
    }

    this.multiSortMeta = event.multiSortMeta || [];
    this.filterValue = typeof event.globalFilter === 'string' ? event.globalFilter : '';
    this.filtersMetadata = event.filters || {};
    this.cargar();
  }

  cargar(): void {
    const now = Date.now();
    if (now - this.lastErrorTimestamp > this.ERROR_RESET_TIMEOUT) {
      this.errorCount = 0;
    }

    if (this.errorCount >= this.MAX_ERROR_COUNT) {
      this.loading.set(false);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: `Se alcanzó el límite de reintentos (${this.MAX_ERROR_COUNT}). Por favor, recarga la página o contacta al administrador.`,
        life: 8000,
        sticky: true
      });
      return;
    }

    this.loading.set(true);

    this.ordenService.findAll(
      this.paginate,
      this.paginaActual,
      this.nroFilasPorPagina,
      this.filterValue || '',
      this.multiSortMeta,
      this.filtersMetadata
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.loading.set(false);
          this.errorCount = 0;
          this.lastErrorTimestamp = 0;

          const members = resp['hydra:member'] || resp.member || resp;
          const ordenesMapeadas = members.map((orden: any) => ({
            id: orden.id,
            numero: orden.numero,
            vigenciaDesde: orden.vigenciaDesde,
            vigenciaHasta: orden.vigenciaHasta,
            estado: orden.estado,
            tipoOrden: orden.tipoOrden,
            montoTotal: orden.montoTotal,
            ordenClientes: orden.ordenClientes || []
          }));

          this.ordenes.set(ordenesMapeadas);
          this.totalRecords.set(resp['hydra:totalItems'] || resp.totalItems || ordenesMapeadas.length);
        },
        error: (error: any) => {
          this.loading.set(false);
          this.errorCount++;
          this.lastErrorTimestamp = Date.now();

          console.error(`Error al recuperar órdenes (intento ${this.errorCount}/${this.MAX_ERROR_COUNT}):`, error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar las órdenes. Intentá nuevamente.',
            life: 4000
          });
        }
      });
  }

  clear(table: Table): void {
    table.clear();
  }

  getClientePrincipal(orden: Orden): string {
    const principal = orden.ordenClientes?.find(cliente => cliente.rol === 'principal');
    if (!principal?.cliente) {
      return '-';
    }
    if (typeof principal.cliente === 'string') {
      return principal.cliente;
    }
    return principal.cliente.razonSocial || principal.cliente.nombreFantasia || '-';
  }

  getEstadoLabel(estado?: string | null): string {
    if (!estado) return '-';
    const labels: Record<string, string> = {
      borrador: 'Borrador',
      confirmada: 'Confirmada',
      anulada: 'Anulada',
      pendiente_aprobacion: 'Pendiente',
      rechazada: 'Rechazada',
      aprobada: 'Aprobada',
      programada: 'Programada',
      compaginada: 'Compaginada',
      exportada: 'Exportada',
      en_emision: 'En emisión',
      emision_parcial: 'Emisión parcial',
      emitida: 'Emitida',
      pendiente_facturacion: 'Pendiente facturación',
      facturacion_parcial: 'Facturación parcial',
      facturada: 'Facturada',
      cancelada: 'Cancelada'
    };
    return labels[estado] || estado;
  }

  getEstadoBadgeClass(estado?: string | null): string {
    if (!estado) return 'app-badge';
    const map: Record<string, string> = {
      borrador: 'app-badge',
      confirmada: 'app-badge-success',
      anulada: 'app-badge-danger',
      pendiente_aprobacion: 'app-badge-warning',
      rechazada: 'app-badge-danger',
      aprobada: 'app-badge-success',
      programada: 'app-badge-primary',
      compaginada: 'app-badge-primary',
      exportada: 'app-badge-primary',
      en_emision: 'app-badge-warning',
      emision_parcial: 'app-badge-warning',
      emitida: 'app-badge-success',
      pendiente_facturacion: 'app-badge-warning',
      facturacion_parcial: 'app-badge-warning',
      facturada: 'app-badge-success',
      cancelada: 'app-badge-danger'
    };
    return map[estado] || 'app-badge';
  }

  navigateToCreateOrden(): void {
    this.router.navigate(['/ordenes/nueva']);
  }

  editarOrden(id: number | undefined): void {
    if (!id) return;
    this.router.navigate([`/ordenes/${id}/editar`]);
  }

  exportarOrden(orden: Orden): void {
    if (!orden.id) return;

    this.ordenService.exportJson(orden.id).subscribe({
      next: (payload) => {
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `orden-${orden.numero || orden.id}.json`;
        link.click();
        URL.revokeObjectURL(url);
        this.messageService.add({
          severity: 'success',
          summary: 'Exportación lista',
          detail: 'Se descargó el JSON de la orden.',
          life: 3000
        });
      },
      error: (error) => {
        console.error('Error al exportar orden:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo exportar la orden.',
          life: 3000
        });
      }
    });
  }
}
